<template>
  <div class="ventrata-ticket-selector-container">
    <div>
      <h4 class="ticket-selector-title">
        Select Tickets & Quantity
      </h4>
      <div v-if="unitsWithPrice.length > 0">
        <div v-for="unit in unitsWithPrice" :key="unit.unitId" class="unit-block-wrapper">
          <div class="unit-block">
            <div class="unit-title">
              {{ getUnitText(unit) }}
            </div>
            <div class="unit-price">
              {{ parsePrice(unit) }}
            </div>
          </div>
          <span v-if="showTicketNotEnough(unit.unitId)" class="ticket-error">Not enough ticket left</span>
          <PaxInput
            class="unit-pax-input"
            :hide-icon="true"
            :value="getPax(unit.unitId)"
            :custom-style="{ 'justify-content': 'flex-end' }"
            :max-pax="getMaxPax(unit.unitId)"
            @input="(e) => onHandlePaxChange(e, unit.unitId)"
          />
        </div>
      </div>
    </div>
    <LoadingBar :loading="loading" class="ventrata-ticket-selector-loading" />
  </div>
</template>

<script>
import PaxInput from '@/components/utils/PaxInput'
import LoadingBar from '@/components/utils/LoadingBar'
import { addDetailedCurrencySymbol } from '@/helpers/price'
import { supportedTickets, computeMaxTickets, sortByCustomOrder, accompaniedTicketToAdd } from '@/helpers/ventrata'

export default {
  name: 'VentrataTicketSelector',
  components: {
    PaxInput,
    LoadingBar
  },
  props: {
    product: {
      type: Object,
      require: true
    },
    selectedTime: {
      type: Object,
      require: false
    },
    currency: {
      type: String,
      require: true
    },
    currentSelectedUnits: {
      type: Array,
      require: false
    }
  },
  data () {
    return {
      selectedUnits: [],
      loading: false
    }
  },
  computed: {
    totalTicketsNumber () {
      return this.selectedTime.vacancies
    },
    unitsMaxPaxLimits () {
      return computeMaxTickets({ selectedUnits: this.selectedUnits, totalTicketsNumber: this.totalTicketsNumber, unitsWithPrice: this.unitsWithPrice })
    },
    units () {
      return this.product?.options?.[0].units ?? []
    },
    unitsWithPrice () {
      const unitPricing = this.selectedTime.unitPricing
      const units = unitPricing?.map((item) => {
        const unitId = item.unitId
        const unitDetail = this.units.find(u => u.id === unitId)
        // console.log('item :>> ', item)

        return {
          unitId,
          title: unitDetail?.title,
          subtitle: unitDetail?.subtitle,
          original: item.original,
          currency: item.currency
        }
      })
      const filteredTickets = units.filter( unit => supportedTickets.includes(unit.title?.toLowerCase()))
      return sortByCustomOrder(filteredTickets)
    }
  },
  watch: {
    selectedTime (newVal) {
      this.onCommitUnits()
    },
    currentSelectedUnits (newVal, oldVal) {
      console.log('newVal :>> ', newVal)
      console.log('oldVal :>> ', oldVal)
      if (newVal?.length === 0 && oldVal?.length > 0) {
        this.resetUnits()
      }
    }
  },
  methods: {
    resetUnits () {
      this.selectedUnits = []
      this.onCommitUnits()
    },
    parsePrice (unit) {
      if (unit.original === 0) {
        return 'Free'
      }
      return addDetailedCurrencySymbol(this.currency, (unit.original / 100).toFixed(2))
    },
    getPax (unitId) {
      const unitItem = this.selectedUnits.find(u => u.unitId === unitId)
      return unitItem?.pax ?? 0
    },
    getMaxPax (unitId) {
      return this.unitsMaxPaxLimits[unitId] ?? this.totalTicketsNumber
    },
    showTicketNotEnough (unitId) {
      const pax = this.getPax(unitId)
      const maxPax = this.getMaxPax(unitId)
      return maxPax < pax
    },
    onHandlePaxChange (pax, unitId) {
      let autoTicketAddTriggered = false
      const unitItem = this.selectedUnits.find(u => u.unitId === unitId)
      // if this is Infant / Child ticket, an Adult ticket should be auto added
      const unitInfo = this.units.find(u=> u.id === unitId)
      // auto add Adult ticket if cur ticket is Child or Infant
      const unitIdNeedToAdd = accompaniedTicketToAdd(unitInfo, this.unitsWithPrice, this.selectedUnits)
      console.log('unitInfo :>> ', unitInfo)
      const accompaniedBy = unitInfo?.restrictions?.accompaniedBy
      if (unitIdNeedToAdd && pax > 0) {
        // console.log('needToAddTicket unitId :>> ', unitIdNeedToAdd)
        const needToAddUnit = this.selectedUnits.find(u => u.unitId === unitIdNeedToAdd)
        if (!needToAddUnit) {
          this.selectedUnits.push({
            unitId: unitIdNeedToAdd,
            pax: 1,
          })
        } else {
          needToAddUnit.pax = 1
        }
        autoTicketAddTriggered = true
      } else if (accompaniedBy?.length > 0 && pax === 0) {
        this.$emit('cancelAutoWarning')
      }
      if (!unitItem) {
        this.selectedUnits.push({
          unitId,
          pax,
        })
      } else {
        // also need to check when pax was 0
        unitItem.pax = pax
      }

      console.log('this.selectedUnits :>> ', this.selectedUnits)
      if (autoTicketAddTriggered) {
        this.$emit('autoAddWarning')
      }
      this.onCommitUnits()
    },
    onCommitUnits () {
      const commitUnits = this.selectedUnits.map((item) => {
        const unitId = item.unitId
        const currencyPrice = this.selectedTime.unitPricing.find(unit => unit.unitId === unitId)
        return {
          unitId,
          pax: item.pax,
          unitType: currencyPrice.unitType,
          original: currencyPrice.original,
          currency: currencyPrice.currency
        }
      })
      console.log('commitUnits :>> ', commitUnits)
      this.$emit('updateUnit', commitUnits)
    },
    getUnitText (unit) {
      if (unit.subtitle) {
        return `${unit.title} (${unit.subtitle})`
      } else {
        return unit.title
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ventrata-ticket-selector-container {
  position: relative;
  padding: 16px;
  margin-top: 16px;
  border-radius: 4px;
  background-color: $text-white;

  .ticket-selector-title {
    margin: 0 0 32px 0;
    @include font__header-5();
  }

  .unit-block-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    border-bottom: 1px solid white;
    .unit-block {
      min-width: 180px;
      .unit-title {
        margin: 0;
        @include font__paragraph-1--bold();
        color: $brand-primary-color;
      }
      .unit-price {
        @include font__small-text-1--regular();
        margin: 6px 0 0;
      }
    }
    .ticket-error {
      color: $brand-purple;
    }
    .unit-pax-input {
      width: auto;
    }
  }

  .ventrata-ticket-selector-loading {
    position: absolute;
    width: 100%;
    height: 100%;
  }
}

@include max-width($mobile) {
  .ventrata-ticket-selector-container {
    padding: 16px 8px;

    .ticket-selector-title {

    }
  }
}
</style>
